import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../lib/payrollEngine';
import type { Bonus, BonusType } from '../lib/supabase';

const TYPE_LABELS: Record<BonusType, string> = {
  fixed: 'Monto Fijo', percentage: 'Porcentaje', performance: 'Por Desempeño',
};

export function BonusesPage() {
  const { company } = useAuth();
  const [bonuses, setBonuses] = useState<Bonus[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Bonus>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => { if (company) load(); }, [company]);

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('bonuses').select('*').eq('company_id', company!.id).order('created_at', { ascending: false });
    setBonuses(data || []);
    setLoading(false);
  }

  function openNew() {
    setEditing({ type: 'fixed', value: 0, is_active: true, applies_to: 'all', name: '', description: '' });
    setModalOpen(true);
  }
  function openEdit(b: Bonus) { setEditing({ ...b }); setModalOpen(true); }

  async function handleSave() {
    if (!editing.name || !editing.type || editing.value === undefined) { showToast('Completa todos los campos'); return; }
    setSaving(true);
    if (editing.id) {
      await supabase.from('bonuses').update(editing).eq('id', editing.id);
    } else {
      await supabase.from('bonuses').insert({ ...editing, company_id: company!.id });
    }
    setSaving(false);
    setModalOpen(false);
    showToast(editing.id ? 'Bono actualizado' : 'Bono creado');
    load();
  }

  async function toggleActive(b: Bonus) {
    await supabase.from('bonuses').update({ is_active: !b.is_active }).eq('id', b.id);
    showToast(b.is_active ? 'Bono pausado' : 'Bono activado');
    load();
  }

  async function handleDelete(id: string) {
    if (!window.confirm('¿Eliminar este bono?')) return;
    await supabase.from('bonuses').delete().eq('id', id);
    showToast('Bono eliminado');
    load();
  }

  const totalImpact = bonuses.filter(b => b.is_active && b.type === 'fixed').reduce((s, b) => s + b.value, 0);

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {toast && (
        <div className="fixed top-6 right-6 z-50 animate-fade-in bg-green-600 text-white px-5 py-3 rounded-xl shadow-xl text-sm font-bold flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px]">check_circle</span>{toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">Bonos e Incentivos</h1>
          <p className="text-on-surface-variant mt-1">Configura las reglas de bonificaciones que se aplicarán en cada planilla</p>
        </div>
        <button onClick={openNew} className="btn-primary text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 flex items-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all">
          <span className="material-symbols-outlined text-[20px]">add</span>
          Nuevo esquema
        </button>
      </div>

      {/* Impact summary */}
      {bonuses.filter(b => b.is_active).length > 0 && (
        <div className="bg-primary-container text-on-primary-container p-6 rounded-2xl relative overflow-hidden shadow-xl">
          <div className="relative z-10">
            <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">Impacto estimado en próxima planilla (bonos fijos)</p>
            <h3 className="text-3xl font-extrabold font-headline">{formatCurrency(totalImpact)} <span className="text-sm font-normal opacity-70">por empleado</span></h3>
            <div className="mt-4 flex gap-6 text-sm">
              <span><strong>{bonuses.filter(b => b.is_active).length}</strong> esquemas activos</span>
              <span><strong>{bonuses.filter(b => !b.is_active).length}</strong> pausados</span>
            </div>
          </div>
          <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
        </div>
      )}

      {/* Bonuses grid */}
      {loading ? (
        <div className="py-12 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : bonuses.length === 0 ? (
        <div className="card py-16 text-center">
          <span className="material-symbols-outlined text-4xl text-outline-variant">redeem</span>
          <p className="text-on-surface-variant text-sm mt-2">No hay esquemas de bonos configurados</p>
          <button onClick={openNew} className="mt-4 text-primary text-sm font-bold hover:underline">+ Crear el primero</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {bonuses.map(b => (
            <div key={b.id} className="card p-6 group hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className={`p-3 rounded-xl ${b.is_active ? 'bg-primary/5' : 'bg-surface-container-high'}`}>
                  <span className={`material-symbols-outlined ${b.is_active ? 'text-primary' : 'text-on-surface-variant'}`}>
                    {b.type === 'fixed' ? 'payments' : b.type === 'percentage' ? 'percent' : 'trending_up'}
                  </span>
                </div>
                <span className={b.is_active ? 'badge-neutral' : 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-surface-container-highest text-on-surface-variant uppercase tracking-wide'}>
                  {b.is_active ? 'Activo' : 'Pausado'}
                </span>
              </div>
              <h3 className="font-bold text-lg text-on-surface mb-1">{b.name}</h3>
              <p className="text-xs text-on-surface-variant mb-4">{b.description || 'Sin descripción'}</p>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-[10px] text-on-surface-variant uppercase font-bold tracking-tighter">Tipo</p>
                  <p className="text-sm font-medium">{TYPE_LABELS[b.type]}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-on-surface-variant uppercase font-bold tracking-tighter">Valor</p>
                  <p className="text-lg font-bold text-primary">
                    {b.type === 'percentage' ? `${b.value}%` : formatCurrency(b.value)}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-5 pt-4 border-t border-outline-variant/10 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEdit(b)} className="flex-1 text-xs font-bold text-primary hover:bg-primary/5 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1">
                  <span className="material-symbols-outlined text-[16px]">edit</span>Editar
                </button>
                <button onClick={() => toggleActive(b)} className="flex-1 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1">
                  <span className="material-symbols-outlined text-[16px]">{b.is_active ? 'pause' : 'play_arrow'}</span>
                  {b.is_active ? 'Pausar' : 'Activar'}
                </button>
                <button onClick={() => handleDelete(b.id)} className="p-1.5 text-error hover:bg-error-container/30 rounded-lg transition-colors">
                  <span className="material-symbols-outlined text-[16px]">delete</span>
                </button>
              </div>
            </div>
          ))}

          {/* Add card */}
          <div onClick={openNew} className="card p-6 border-dashed border-2 border-outline-variant/30 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-surface-container-low hover:border-primary/30 transition-all group min-h-[200px]">
            <span className="material-symbols-outlined text-outline-variant group-hover:text-primary transition-colors text-3xl">add_circle</span>
            <span className="text-sm font-medium text-on-surface-variant">Agregar nuevo esquema</span>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface-container-lowest rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between p-6 border-b border-outline-variant/10">
              <h2 className="text-xl font-bold font-headline">{editing.id ? 'Editar bono' : 'Nuevo bono'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-surface-container-high rounded-lg transition-colors">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Nombre del bono *</label>
                <input className="input-base" value={editing.name || ''} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} placeholder="Ej: Bono por desempeño Q4" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Descripción</label>
                <textarea className="input-base h-20 resize-none" value={editing.description || ''} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} placeholder="Descripción de cómo se aplica..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Tipo</label>
                  <select className="input-base" value={editing.type || 'fixed'} onChange={e => setEditing(p => ({ ...p, type: e.target.value as BonusType }))}>
                    <option value="fixed">Monto Fijo</option>
                    <option value="percentage">Porcentaje del sueldo</option>
                    <option value="performance">Por desempeño</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                    Valor {editing.type === 'percentage' ? '(%)' : '(S/.)'}
                  </label>
                  <input className="input-base" type="number" step="0.01" min="0" value={editing.value || 0} onChange={e => setEditing(p => ({ ...p, value: parseFloat(e.target.value) || 0 }))} />
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-surface-container-low rounded-xl">
                <input type="checkbox" id="active" checked={editing.is_active !== false} onChange={e => setEditing(p => ({ ...p, is_active: e.target.checked }))} className="accent-primary w-4 h-4" />
                <label htmlFor="active" className="text-sm font-medium cursor-pointer">Bono activo (se aplicará en la próxima planilla)</label>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-outline-variant/10">
              <button onClick={() => setModalOpen(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-outline-variant/30 text-sm font-bold text-on-surface-variant hover:bg-surface-container-low transition-all">Cancelar</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary text-white py-2.5 rounded-lg font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-60">
                {saving ? 'Guardando...' : (editing.id ? 'Actualizar' : 'Crear bono')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
